// NIGHT-DIRECTSTAFF-2026-10-22 §10: the Reports page -- this week / last 4 weeks / this month, numbers only, never a name.
// SAME DEFINITIONS AS THE AGENTS WINDOW'S BRIEF (pt-agents agents/morning_brief.py) wherever it has one, so the two never
// disagree; where it has none, the definition is written here and in the page's footnote.
//   leads         created in the period; not spam (spam_marked_at), not test-marked, not merged, not a deploy check
//                 (form_type deploy_verification -- the brief's TEST_FORM_TYPES); by source_site
//   consent rate  the brief's CONSENT_FORM_TYPES (job_apply, job_alert, lead, profile): sms_consent true / all; fewer
//                 than 10 leads (MIN_RATE_N) -> the count only, no rate
//   first text    per lead with a phone: its thread's first agent text (the thread on the lead's number started no
//                 earlier than 5 minutes before the lead -- the brief's JOIN_TOLERANCE). "Texted" = status sent or
//                 delivered with no error_code, or a legacy row with no status (CONTACT_STATUSES); "delivered" = that
//                 first text carries a delivery receipt (status delivered, no error_code)
//   reply in 24 h the brief's section F: every inbound message (candidate / user) in the period and the first reply on that
//                 thread after it -- a person's row, or the agent's sent / delivered / legacy text; a failed or undelivered
//                 text is not a reply. Replied within 24 h · no reply after 24 h · still inside the 24 h
//   hand-offs     alerts raised in the period (agent_notifications), the hand-back trace rows left out (they are events,
//                 not alerts); facility requests and call requests are counted on their own lines too
//   placements    start_date in the period (started), end_date in the period (ending) -- on contract statuses
//   emails        the CRM's sends (activity_log type email: Sent / Bounced / Opted out) and the agent's first emails
//                 (agent_lead_decisions first_email_sent / email_suppressed / email_unsubscribed)
//   exam pass     attempts submitted in the period: passed / submitted
export const PERIODS = { week: 'This week', four: 'Last 4 weeks', month: 'This month' };
export const CONSENT_FORM_TYPES = ['job_apply', 'job_alert', 'lead', 'profile'];
export const TEST_FORM_TYPES = ['deploy_verification'];
export const MIN_RATE_N = 10;
const JOIN_TOLERANCE_MS = 5 * 60e3;
const DAY = 864e5;
const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const t = (iso) => { const x = Date.parse(iso || ''); return Number.isNaN(x) ? null : x; };
const national = (v) => { const d = s(v).replace(/\D/g, ''); return d.length === 11 && d.startsWith('1') ? d.slice(1) : d.length === 10 ? d : ''; };
const pct = (a, b) => (b ? `${Math.round((a / b) * 1000) / 10}%` : '—');
export const rateCell = (a, b) => (b < MIN_RATE_N ? `n=${b}` : `${pct(a, b)} (${a} of ${b})`);

/** The period's start (ms), in Eastern time: Monday 00:00 for the week, the 1st for the month; now - 28 days for "four". */
export function periodStart(period, now = Date.now()) {
  if (period === 'four') return now - 28 * DAY;
  const et = new Date(new Date(now).toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const offset = now - et.getTime();   // ET -> UTC difference, DST included
  const d = new Date(et.getFullYear(), et.getMonth(), period === 'month' ? 1 : et.getDate() - ((et.getDay() + 6) % 7));
  return d.getTime() + offset;
}

const isSpam = (l) => !!(l && (l.spam_marked_at || l.spam_reason === 'spam'));
const isTestLead = (l) => !!(l && (l.test_marked_at || TEST_FORM_TYPES.includes(s(l.form_type))));
const isMergedLead = (l) => !!(l && l.merged_into);
const inP = (iso, from, to) => { const x = t(iso); return x !== null && x >= from && x <= to; };
const day = (d) => (d ? Date.parse(`${s(d).slice(0, 10)}T12:00:00Z`) : null);

const isContact = (m) => (['sent', 'delivered'].includes(s(m.status)) && !m.error_code) || (!m.status && !m.error_code);
const isReply = (m) => s(m.role) === 'human' || (s(m.role) === 'agent' && isContact(m));

/**
 * inputs (all optional): leads, conversations, messages (agent_messages: conversation_id, role, status, error_code,
 * created_at), notifications, placements, emailRows (activity_log type email), decisions, examAttempts; isFacility(l).
 * -> { from, to, tables: [{ id, title, columns, rows }] } -- every cell a number or a percentage, never a name.
 */
export function buildReports({ period = 'week', now = Date.now(), leads = [], conversations = [], messages = [], notifications = [], placements = [],
  emailRows = [], decisions = [], examAttempts = [], isFacility = () => false } = {}) {
  const from = periodStart(period, now), to = now;
  const L = leads.filter((l) => inP(l.created_at, from, to) && !isSpam(l) && !isTestLead(l) && !isMergedLead(l));
  // leads by source
  const bySource = new Map();
  for (const l of L) { const k = s(l.source_site) || 'unknown'; bySource.set(k, (bySource.get(k) || 0) + 1); }
  const sourceRows = [...bySource].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([k, n]) => [k, n]);
  // consent
  const cl = L.filter((l) => CONSENT_FORM_TYPES.includes(s(l.form_type)));
  const consented = cl.filter((l) => l.sms_consent === true).length;
  // first text per lead (thread on the lead's number, started no earlier than 5 min before the lead)
  const convByPhone = new Map();
  for (const c of conversations) { const p = national(c.candidate_phone); if (p) { if (!convByPhone.has(p)) convByPhone.set(p, []); convByPhone.get(p).push(c); } }
  const msgsByConv = new Map();
  for (const m of [...messages].sort((a, b) => s(a.created_at).localeCompare(s(b.created_at)))) { if (!msgsByConv.has(m.conversation_id)) msgsByConv.set(m.conversation_id, []); msgsByConv.get(m.conversation_id).push(m); }
  let texted = 0, delivered = 0, withPhone = 0;
  for (const l of L) {
    const p = national(l.phone);
    if (!p) continue;
    withPhone += 1;
    const lt = t(l.created_at);
    const convs = (convByPhone.get(p) || []).filter((c) => { const st = t(c.started_at); return st === null || st >= lt - JOIN_TOLERANCE_MS; });
    const first = convs.flatMap((c) => msgsByConv.get(c.id) || []).filter((m) => s(m.role) === 'agent').sort((a, b) => s(a.created_at).localeCompare(s(b.created_at)))[0];
    if (first && isContact(first)) { texted += 1; if (s(first.status) === 'delivered' && !first.error_code) delivered += 1; }
  }
  // reply latency (brief F)
  let inbound = 0, within = 0, late = 0, waiting = 0;
  for (const [, list] of msgsByConv) {
    list.forEach((m, i) => {
      if (!['candidate', 'user'].includes(s(m.role)) || !inP(m.created_at, from, to)) return;
      inbound += 1;
      const mt = t(m.created_at);
      const r = list.slice(i + 1).find(isReply);
      if (r && t(r.created_at) - mt <= DAY) within += 1;
      else if (now - mt < DAY && !r) waiting += 1;
      else late += 1;
    });
  }
  // alerts
  const N = notifications.filter((n) => inP(n.created_at, from, to) && s(n.reason) !== 'handed_back');
  const facilityN = L.filter(isFacility).length;
  const callN = N.filter((n) => s(n.reason) === 'call_requested').length;
  // placements
  const ON = ['started', 'ending', 'extended', 'ended'];
  const started = placements.filter((p) => ON.includes(s(p.status)) && inP(p.start_date && new Date(day(p.start_date)).toISOString(), from, to)).length;
  const ending = placements.filter((p) => ON.includes(s(p.status)) && inP(p.end_date && new Date(day(p.end_date)).toISOString(), from, to)).length;
  // emails
  const E = emailRows.filter((a) => s(a.type) === 'email' && inP(a.created_at, from, to)).map((a) => (/· (Sent|Bounced|Opted out)(?: ·|$)/.exec(s(a.note)) || [])[1]);
  const Dd = decisions.filter((d) => inP(d.created_at, from, to));
  const dc = (x) => Dd.filter((d) => s(d.decision) === x).length;
  // exams
  const A = examAttempts.filter((a) => inP(a.submitted_at, from, to));
  const passed = A.filter((a) => a.passed).length;
  return {
    from: new Date(from).toISOString(), to: new Date(to).toISOString(),
    tables: [
      { id: 'sources', title: 'Leads by source', columns: ['source', 'leads'], rows: [...sourceRows, ['total', L.length]] },
      { id: 'funnel', title: 'Leads to first text to reply', columns: ['measure', 'value'], rows: [
        ['leads', L.length],
        ['consent rate (profile, apply, alert, lead forms)', rateCell(consented, cl.length)],
        ['leads with a phone', withPhone],
        ['texted (first text sent or delivered)', texted],
        ['first text delivered', rateCell(delivered, texted)],
        ['inbound messages', inbound],
        ['replied within 24 h', rateCell(within, inbound)],
        ['no reply after 24 h', late],
        ['still inside 24 h', waiting],
      ] },
      { id: 'handoffs', title: 'Hand-offs and requests', columns: ['measure', 'count'], rows: [
        ['alerts raised (hand-backs left out)', N.length], ['facility requests', facilityN], ['call requested', callN]] },
      { id: 'placements', title: 'Placements', columns: ['measure', 'count'], rows: [['started', started], ['ending', ending]] },
      { id: 'emails', title: 'Emails', columns: ['measure', 'count'], rows: [
        ['sent from the CRM', E.filter((x) => x === 'Sent').length], ['bounced (CRM sends)', E.filter((x) => x === 'Bounced').length],
        ['marked No email (CRM)', E.filter((x) => x === 'Opted out').length], ['agent first emails sent', dc('first_email_sent')],
        ['bounced (agent emails)', dc('email_suppressed')], ['unsubscribed (job alerts)', dc('email_unsubscribed')]] },
      { id: 'exams', title: 'Exams', columns: ['measure', 'value'], rows: [['attempts submitted', A.length], ['pass rate', rateCell(passed, A.length)]] },
    ],
  };
}

/** One table -> CSV text (quoted where needed; numbers only, by construction). */
export function toCsv(table) {
  const q = (v) => { const x = String(v === null || v === undefined ? '' : v); return /[",\n]/.test(x) ? `"${x.replace(/"/g, '""')}"` : x; };
  return [table.columns, ...table.rows].map((r) => r.map(q).join(',')).join('\n') + '\n';
}
